import React from 'react';
import { useSelector } from 'react-redux';
import { getAllMotifs } from 'cards/selectors/cardCatalogSelector';
import { Motif } from 'cards/components/CardListing/types';
import {
  FILTER_TYPE_BOOLEAN,
  FILTER_KEYS,
} from 'cards/components/CardListing/CardListingRedesign/CardListingFilter/constants';
import {
  FilterKey,
  FilterValue,
  CardListingSearchFilters,
} from 'cards/components/CardListing/CardListingRedesign/CardListingFilter/FilterTypes';
import { existenceCheck, getLabel } from 'cards/components/CardListing/CardListingRedesign/utils';
import { StyledFilterPill } from '../FilterStyles.styles';
import { FilterPillsContainer, FilterPillClearAll } from './FilterPills.style';

interface FilterPillsProps {
  clearFilters: () => void;
  updateFilter: (filterKey: FilterKey, filterValue?: FilterValue) => void;
  searchFilters: CardListingSearchFilters;
  className?: string;
}

export const FilterPills: React.FC<FilterPillsProps> = ({
  clearFilters,
  updateFilter,
  searchFilters,
  className,
}) => {
  const allMotifs: Motif[] = useSelector(getAllMotifs);
  const filterKeys = Object.keys(FILTER_KEYS) as Array<FilterKey>;

  const activeFilters = filterKeys.filter(filter => {
    const currentValue = searchFilters[filter];
    return existenceCheck(currentValue);
  });

  const clearAction = (filter: FilterKey, tagVal?: string) => {
    if (!tagVal) return;

    const newVal = [...(searchFilters[filter] as string[])].filter(
      val => val.toLowerCase() !== tagVal.toLowerCase()
    );
    updateFilter(filter, newVal);
  };

  const allTags: React.ReactNode[] = activeFilters
    .map(filter => {
      if (FILTER_KEYS[filter].filterType === FILTER_TYPE_BOOLEAN) {
        return (
          <StyledFilterPill
            key={FILTER_KEYS[filter].label}
            value={FILTER_KEYS[filter].label}
            label={FILTER_KEYS[filter].label}
            handlePillClick={() => updateFilter(filter, FILTER_KEYS[filter].defaultValue)}
          />
        );
      }

      if (filter === 'multiColor' || filter === 'seo_colors' || filter === 'silhouettes') {
        return searchFilters[filter]?.map(multi => (
          <StyledFilterPill
            key={multi}
            value={multi}
            label={multi && getLabel(multi)}
            handlePillClick={() => clearAction(filter, multi)}
          />
        ));
      }

      if (filter === 'motifs') {
        return searchFilters[filter]?.map(style => {
          const motifLabel = allMotifs.find(motif => motif.key.toUpperCase() === style);
          if (!motifLabel) return null;

          return (
            <StyledFilterPill
              key={motifLabel.key}
              value={motifLabel.key}
              label={motifLabel.name || ''}
              handlePillClick={() => clearAction(filter, motifLabel.key)}
            />
          );
        });
      }

      return null;
    })
    .filter(tag => tag); // filter out null tag

  if (allTags.length === 0) return null;

  return (
    <FilterPillsContainer className={className}>
      <FilterPillClearAll onClick={clearFilters}>Clear</FilterPillClearAll>
      {allTags}
    </FilterPillsContainer>
  );
};
